// Helpers de presentación de la campanita.
//
// Viven fuera de los componentes a propósito: son funciones puras sobre la lista
// de notificaciones, no dependen del ciclo de vida de React y se pueden testear
// sin montar nada.

const MINUTO = 60 * 1000
const HORA = 60 * MINUTO
const DIA = 24 * HORA

const arrancaElDia = (fecha) => {
	const d = new Date(fecha)
	d.setHours(0, 0, 0, 0)
	return d
}

/**
 * Fecha corta para la fila: "hace 5 min", "ayer, 18:40", "lun, 09:12".
 *
 * La exacta con segundos que se mostraba antes no la lee nadie, y ocupaba el
 * lugar donde ahora va el hallazgo. Queda igual en el `title` del elemento, que
 * es donde importa cuando de verdad hace falta el momento preciso.
 */
export const fechaRelativa = (timestamp, ahora = new Date()) => {
	const fecha = timestamp instanceof Date ? timestamp : new Date(timestamp)
	if (isNaN(fecha.getTime())) return ''

	const delta = ahora - fecha

	// Un reloj de cliente adelantado produce timestamps del servidor "en el
	// futuro". Sin este caso, la resta da negativo y sale "hace -3 min".
	if (delta < MINUTO) return 'recién'
	if (delta < HORA) return `hace ${Math.floor(delta / MINUTO)} min`

	const diasDeDiferencia = Math.round((arrancaElDia(ahora) - arrancaElDia(fecha)) / DIA)
	const hora = fecha.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

	if (diasDeDiferencia === 0) {
		return delta < 6 * HORA ? `hace ${Math.floor(delta / HORA)} h` : hora
	}
	if (diasDeDiferencia === 1) return `ayer, ${hora}`
	if (diasDeDiferencia < 7) {
		const dia = fecha.toLocaleDateString([], { weekday: 'short' })
		return `${dia}, ${hora}`
	}
	return fecha.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Fecha completa, para el tooltip. */
export const fechaExacta = (timestamp) => {
	const fecha = timestamp instanceof Date ? timestamp : new Date(timestamp)
	return isNaN(fecha.getTime()) ? '' : fecha.toLocaleString()
}

/**
 * Tramo temporal al que pertenece una notificación. Es lo que separa la lista en
 * bloques: con quince avisos, saber cuál es de hoy exigía leer fecha por fecha.
 */
export const tramoDe = (timestamp, ahora = new Date()) => {
	const fecha = timestamp instanceof Date ? timestamp : new Date(timestamp)
	if (isNaN(fecha.getTime())) return 'antes'

	const dias = Math.round((arrancaElDia(ahora) - arrancaElDia(fecha)) / DIA)
	if (dias <= 0) return 'hoy'
	if (dias === 1) return 'ayer'
	if (dias < 7) return 'semana'
	return 'antes'
}

export const etiquetaDeTramo = (tramo, t) => {
	switch (tramo) {
		case 'hoy':
			return t('today', 'Hoy')
		case 'ayer':
			return t('yesterday', 'Ayer')
		case 'semana':
			return t('this_week', 'Esta semana')
		default:
			return t('earlier', 'Antes')
	}
}

/**
 * Tipos que se agrupan por hallazgo. Son los dos que el mismo hallazgo puede
 * repetir: te nombran varias veces en el mismo comentario, o te reasignan el
 * mismo hallazgo varias veces seguidas.
 *
 * Un "expirado" y un "cambió de estado" del mismo ticket NO se agrupan entre sí
 * ni con estos: son avisos distintos, y meterlos en una fila haría que borrar
 * uno se llevara el otro sin que se vea.
 */
const TIPOS_AGRUPABLES = ['mention', 'assigned']

/**
 * Clave de agrupación de una notificación, o null si va sola.
 *
 * Incluye el tipo además del hallazgo, así una mención y una asignación del
 * mismo ticket siguen siendo dos filas: son dos cosas distintas que pasaron.
 */
export const claveDeGrupo = (notif) => {
	if (!TIPOS_AGRUPABLES.includes(notif?.type)) return null

	const ticketId = notif?.data?.ticketId

	return ticketId ? `${notif.type}:${ticketId}` : null
}

/**
 * Agrupa los avisos repetidos del mismo hallazgo.
 *
 * Es puramente de presentación: NO toca el estado ni fusiona notificaciones. La
 * lista sigue siendo plana, y cada aviso conserva su id, su "leída" y su borrado
 * individual. Lo único que cambia es que la campanita deja de mostrar cinco
 * filas idénticas cuando alguien te nombra cinco veces en el mismo lugar.
 */
export const agruparPorHallazgo = (notificaciones) => {
	const grupos = []
	const porClave = new Map()

	notificaciones.forEach((notif) => {
		const clave = claveDeGrupo(notif)

		if (!clave) {
			grupos.push({ principal: notif, anteriores: [] })
			return
		}

		const existente = porClave.get(clave)
		if (existente) {
			// La lista ya viene ordenada por fecha descendente, así que la
			// primera que se ve es la más nueva y las siguientes son "anteriores".
			existente.anteriores.push(notif)
			return
		}

		const grupo = { principal: notif, anteriores: [] }
		porClave.set(clave, grupo)
		grupos.push(grupo)
	})

	return grupos
}

/**
 * Identidad de una notificación entre pestañas.
 *
 * `key` para las menciones, que se recrean en cada merge con el servidor y por
 * eso no conservan la referencia. `id` para los otros cinco tipos, que desde el
 * `event_id` del backend también es igual en todas las pestañas.
 */
export const idDe = (notif) => notif.key ?? String(notif.id)

/**
 * Si la lista ya tiene un aviso con esta identidad.
 *
 * Se compara con la MISMA regla que la fusión entre pestañas: lo que acá se deje
 * entrar dos veces, allá se colapsa en uno solo, y la campanita mostraría un
 * número distinto según en qué pestaña se mire.
 */
export const yaEstaEnLaLista = (lista, id) =>
	(lista || []).some((n) => idDe(n) === String(id))

/**
 * Fusiona lo que tiene esta pestaña con lo que acaba de guardar otra.
 *
 * El problema que resuelve: cada pestaña persiste la lista ENTERA en cada cambio,
 * así que la última en escribir gana con la foto que tenía en memoria. Leías una
 * notificación en una pestaña, llegaba un aviso a la otra, y la otra reescribía
 * la lista sin tu lectura. Lo mismo con los borrados, que volvían a aparecer.
 *
 * Las dos operaciones son de ida (nadie "des-lee" ni "des-borra"), así que
 * fusionar en vez de reemplazar converge sin importar el orden en que las
 * pestañas escriban:
 *
 *  - lo que está en las dos: leída si CUALQUIERA la marcó leída;
 *  - lo que está sólo en la otra: entra (lo recibió ella y yo no);
 *  - lo que está sólo acá: se conserva si llegó DESPUÉS de mi última escritura,
 *    porque entonces la otra pestaña no podía conocerlo. Si es anterior, yo ya
 *    lo había persistido y la otra guardó una lista sin él: lo borró.
 *
 * @param {number} desde  cuándo escribió esta pestaña por última vez (ms)
 * @param {object} estado el `dismissed` de las menciones, que es la señal de
 *                        borrado explícita y le gana a la heurística de arriba
 */
export const fusionarEntrePestanas = (locales, guardadas, opciones = {}) => {
	const { desde = 0, estado = {} } = opciones

	const porId = new Map((guardadas || []).map((n) => [idDe(n), n]))

	;(locales || []).forEach((n) => {
		const id = idDe(n)
		const guardada = porId.get(id)

		if (guardada) {
			porId.set(id, { ...guardada, read: !!guardada.read || !!n.read })
			return
		}

		if (estado[id]?.dismissed) return

		const llegoDespuesDeMiUltimaEscritura = new Date(n.timestamp).getTime() > desde
		if (llegoDespuesDeMiUltimaEscritura) porId.set(id, n)
	})

	// De la más nueva a la más vieja: es el orden del que depende la agrupación
	// para decidir cuál es la principal de cada grupo.
	return [...porId.values()].sort(
		(a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
	)
}

/**
 * Si dos listas dicen lo mismo. Se compara lo que se persiste y se ve (identidad,
 * orden y leída), no la referencia.
 *
 * Sin esto las pestañas entran en ping-pong: A escribe, B fusiona, B escribe, A
 * fusiona, y así para siempre aunque ninguna haya cambiado nada.
 */
export const mismasNotificaciones = (a, b) =>
	a.length === b.length &&
	a.every((n, i) => idDe(n) === idDe(b[i]) && !!n.read === !!b[i].read)

/** Todas las notificaciones de un grupo, la principal primero. */
export const notificacionesDelGrupo = (grupo) => [grupo.principal, ...grupo.anteriores]

/**
 * Un grupo está sin leer si CUALQUIERA de sus notificaciones lo está.
 *
 * No alcanza con mirar la principal: si la más nueva ya se leyó pero quedó una
 * anterior sin leer, el grupo tiene que seguir con su punto, o el contador de la
 * campanita diría un número que la lista no explica.
 */
export const grupoSinLeer = (grupo) => notificacionesDelGrupo(grupo).some((n) => !n.read)

/**
 * Divide los grupos en tramos con su etiqueta, listos para renderizar.
 * Devuelve [] si no quedó ninguno, para que el llamador muestre el vacío.
 */
export const enTramos = (grupos, t, ahora = new Date()) => {
	const orden = ['hoy', 'ayer', 'semana', 'antes']
	const porTramo = new Map()

	grupos.forEach((grupo) => {
		const tramo = tramoDe(grupo.principal.timestamp, ahora)
		if (!porTramo.has(tramo)) porTramo.set(tramo, [])
		porTramo.get(tramo).push(grupo)
	})

	return orden
		.filter((tramo) => porTramo.has(tramo))
		.map((tramo) => ({
			tramo,
			etiqueta: etiquetaDeTramo(tramo, t),
			grupos: porTramo.get(tramo),
		}))
}

/**
 * Nombre y foto del autor, en el formato que espera AvatarProfile.
 *
 * El backend manda el nombre ya concatenado (`mentioned_by`), no separado en
 * name/lastname, así que va entero como `name`: AvatarProfile arma las iniciales
 * a partir del nombre completo igual.
 */
export const autorDe = (notif) => ({
	name: notif.author?.name || '',
	lastname: '',
	image: notif.author?.image || null,
})
